import { Flex, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SessionTimePicker } from '#modules/finder/features/posts/components/Post/SessionTimeField';
import { SelectLanguages } from '#modules/user/features/profile/components/SelectLanguages';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { GameParameterSelect } from '#features/ui/components/inputs/GameParameterSelect';
import { OptionsGroup } from '#features/ui/components/inputs/OptionsGroup';

/** Local form state only: visual QA never saves account settings or creates sessions. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/selectors')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    busy: search.busy === 'all' ? 'all' : 'partial',
  }),
  component: PreviewRoute,
});

function PreviewRoute() {
  const { lang, busy } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  return (
    <I18nProvider i18n={i18n}>
      <Selectors key={lang} busy={busy === 'all'} locale={lang} />
    </I18nProvider>
  );
}

function Selectors({ busy, locale }: { busy: boolean; locale: string }) {
  const { t } = useLingui();
  const [time, setTime] = useState<string>();
  const [language, setLanguage] = useState(locale);
  const [zone, setZone] = useState('2');
  const [platforms, setPlatforms] = useState<(string | number)[]>(['PC']);
  const { control } = useForm({ defaultValues: { languages: [] as string[] } });
  const [catalog] = useState(() => {
    const client = new QueryClient({ defaultOptions: { queries: { enabled: false } } });
    client.setQueryData(
      ['languages'],
      [
        { code: 'en', name: 'English', nativeName: 'English' },
        { code: 'ru', name: 'Russian', nativeName: 'Русский' },
        { code: 'es', name: 'Spanish', nativeName: 'español' },
        { code: 'de', name: 'German', nativeName: 'Deutsch' },
        { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
      ]
    );
    return client;
  });
  const options = Array.from({ length: 48 }, (_, index) => {
    const hour = Math.floor(index / 2);
    return {
      value: String(index),
      hour,
      label: `${hour % 12 || 12}:${index % 2 ? '30' : '00'} ${hour < 12 ? 'am' : 'pm'}`,
      disabled: busy || index < 6,
    };
  });
  return (
    <Stack p={4} gap={6} maxW="xl" mx="auto">
      <Text textStyle="title">{t`Language`}</Text>
      <GameParameterSelect
        name="locale"
        triggerLabel={t`Language`}
        value={language}
        onChange={(value) => {
          if (typeof value === 'string') setLanguage(value);
        }}
        options={[
          { value: 'en', label: 'English' },
          { value: 'ru', label: 'Русский' },
        ]}
      />
      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
        <Stack gap={0} minW={0} flex="1" flexBasis={48}>
          <Text textStyle="title">{t`dashboard:settings.account.timeZone`}</Text>
          <Text textStyle="caption" color="fg.secondary">
            {t`dashboard:settings.account.currentTime`}: 14:00 (GMT+2)
          </Text>
        </Stack>
        <Flex w="120px" flexShrink={0}>
          <GameParameterSelect
            name="timeZone"
            triggerLabel={t`dashboard:settings.account.timeZone`}
            value={zone}
            onChange={(value) => {
              if (typeof value === 'string') setZone(value);
            }}
            options={Array.from({ length: 27 }, (_, index) => ({
              value: String(index - 12),
              label: `GMT${index < 12 ? '' : '+'}${index - 12}`,
            }))}
          />
        </Flex>
      </Flex>
      <SessionTimePicker
        name="startTime"
        label={t`post:form.startTime`}
        description="7 October · GMT+2"
        value={time}
        onChange={setTime}
        options={options}
      />
      <OptionsGroup
        name="platforms"
        label={t`common:attributes.platform`}
        selectType="multiple"
        variant="pill"
        value={platforms}
        onChange={(value) => {
          if (Array.isArray(value)) setPlatforms(value);
        }}
        options={['PC', 'Xbox', 'PlayStation'].map((value) => ({ value, label: value }))}
      />
      <OptionsGroup
        name="platformCards"
        label={t`common:attributes.platform`}
        selectType="single"
        variant="card"
        defaultValue="PC"
        options={['PC', 'Xbox', 'PlayStation'].map((value) => ({ value, label: value }))}
      />
      <QueryClientProvider client={catalog}>
        <SelectLanguages name="languages" control={control} />
      </QueryClientProvider>
      <GameParameterSelect
        name="disabled"
        label={t`Language`}
        value="en"
        disabled
        options={[{ value: 'en', label: 'English' }]}
      />
    </Stack>
  );
}
